/**
 * Quiet Hours — the four meditation categories.
 *
 * Each category carries:
 *  - identity (slug, title, short description in plain English — no
 *    meditation-app jargon)
 *  - an atmosphere image (light + dark) for the background field
 *  - a palette tuned to its time-of-day / mood
 *  - a breathing pattern (some patterns suit some moods better)
 *
 * Soundscape and voice scripts are wired in their own modules (sound.ts /
 * scripts/*) keyed on the same slug — keeping the metadata here keeps this
 * file readable as the single source of "what are the four rooms in
 * Quiet Hours."
 */

export type QuietHoursCategorySlug =
  | 'morning-calm'
  | 'evening-wind-down'
  | 'anxiety-relief'
  | 'sleep-aid';

export interface BreathingPattern {
  /** seconds for the inhale phase */
  inhale: number;
  /** seconds at the top */
  holdTop: number;
  /** seconds for the exhale phase */
  exhale: number;
  /** seconds at the bottom */
  holdBottom: number;
}

export interface QuietHoursCategoryMeta {
  slug: QuietHoursCategorySlug;
  /** Display title — plain English, warm. */
  title: string;
  /** One-line description shown under the title on the hub. */
  blurb: string;
  /** Description shown on the session config page — slightly longer. */
  longDescription: string;
  /** Public path served by Vite from `apps/games-web/public/`. */
  atmosphereLight: string;
  atmosphereDark: string;
  /** Hex orb color (light theme). */
  orbColorLight: string;
  /** Hex orb color (dark theme). */
  orbColorDark: string;
  /** Hex halo / glow color (light theme). */
  haloColorLight: string;
  /** Hex halo / glow color (dark theme). */
  haloColorDark: string;
  /** Breathing pattern for the visualizer (seconds). */
  breath: BreathingPattern;
  /** Hour-of-day this category is suggested for (start, end). 24h, end-exclusive. */
  suggestedHours: [number, number];
}

export const CATEGORIES: readonly QuietHoursCategoryMeta[] = [
  {
    slug: 'morning-calm',
    title: 'Morning Calm',
    blurb: 'Begin the day quietly.',
    longDescription:
      'A gentle start. A few slow breaths to arrive in the day before anything is asked of you.',
    atmosphereLight: '/generated/quiet-hours/morning-calm-light.png',
    atmosphereDark: '/generated/quiet-hours/morning-calm-dark.png',
    orbColorLight: '#F0BC72',
    orbColorDark: '#FFCB8A',
    haloColorLight: '#E8A352',
    haloColorDark: '#FFB770',
    // Box breathing for morning — 4-4-4-4 is alert and balanced.
    breath: { inhale: 4, holdTop: 4, exhale: 4, holdBottom: 4 },
    suggestedHours: [5, 11],
  },
  {
    slug: 'evening-wind-down',
    title: 'Evening Wind-Down',
    blurb: 'Set the day down.',
    longDescription:
      'A few minutes to soften the day. Let the shoulders ease. Let the room come to you.',
    atmosphereLight: '/generated/quiet-hours/evening-wind-down-light.png',
    atmosphereDark: '/generated/quiet-hours/evening-wind-down-dark.png',
    orbColorLight: '#D6914A',
    orbColorDark: '#FFA85A',
    haloColorLight: '#C97F38',
    haloColorDark: '#FFB770',
    // Slightly longer exhale than inhale — settles the nervous system.
    breath: { inhale: 4, holdTop: 2, exhale: 6, holdBottom: 2 },
    suggestedHours: [17, 22],
  },
  {
    slug: 'anxiety-relief',
    title: 'Anxiety Relief',
    blurb: 'Steady the moment.',
    longDescription:
      'When something feels tight or fast. A few patient breaths to give the body a chance to slow down.',
    atmosphereLight: '/generated/quiet-hours/anxiety-relief-light.png',
    atmosphereDark: '/generated/quiet-hours/anxiety-relief-dark.png',
    orbColorLight: '#7A9FAA',
    orbColorDark: '#92B6C2',
    haloColorLight: '#5A7E92',
    haloColorDark: '#7A92B6',
    // Longer exhale than inhale — activates the vagus nerve.
    breath: { inhale: 4, holdTop: 4, exhale: 6, holdBottom: 2 },
    suggestedHours: [11, 17],
  },
  {
    slug: 'sleep-aid',
    title: 'Sleep Aid',
    blurb: 'A long slow exhale toward rest.',
    longDescription:
      'For the end of the night. Slow, soft breathing to invite sleep — no fanfare, no urgency.',
    atmosphereLight: '/generated/quiet-hours/sleep-aid-light.png',
    atmosphereDark: '/generated/quiet-hours/sleep-aid-dark.png',
    orbColorLight: '#A89FB8',
    orbColorDark: '#9085B0',
    haloColorLight: '#8A7FA0',
    haloColorDark: '#7066A0',
    // Deep slow breathing — extended exhale.
    breath: { inhale: 4, holdTop: 4, exhale: 8, holdBottom: 2 },
    suggestedHours: [22, 5],
  },
] as const;

export const DURATIONS_MIN = [1, 3, 5, 10] as const;
export type DurationMinutes = (typeof DURATIONS_MIN)[number];

export function getCategory(slug: string): QuietHoursCategoryMeta | undefined {
  return CATEGORIES.find((c) => c.slug === slug);
}

export function isDurationMinutes(value: number): value is DurationMinutes {
  return (DURATIONS_MIN as readonly number[]).includes(value);
}

/**
 * Pick a "suggested" category given the current hour. Returns the first
 * category whose `suggestedHours` range contains the hour. For Sleep Aid
 * the range wraps midnight; we handle that explicitly.
 */
export function suggestedForHour(hour: number): QuietHoursCategoryMeta {
  for (const cat of CATEGORIES) {
    const [start, end] = cat.suggestedHours;
    if (start <= end) {
      if (hour >= start && hour < end) return cat;
    } else {
      // wraps midnight (e.g., 22..5)
      if (hour >= start || hour < end) return cat;
    }
  }
  return CATEGORIES[0]!;
}

/** Cycle duration of one full breath, in seconds. */
export function breathCycleSeconds(pattern: BreathingPattern): number {
  return pattern.inhale + pattern.holdTop + pattern.exhale + pattern.holdBottom;
}
